import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Phone, MapPin, ChevronRight, Wrench, PackagePlus, CheckCircle2, History, Crosshair, Image as ImageIcon, Camera } from "lucide-react";


/** Append the JWT to a /api/files/... URL so <img>/<a> tags can load it.
 *  No-op for absolute/external URLs or data: URLs.
 */
function authImg(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const token = localStorage.getItem("access_token") || "";
  const sep = url.includes("?") ? "&" : "?";
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  // Convert relative /api/files/... to absolute URL using REACT_APP_BACKEND_URL.
  const abs = url.startsWith("/") ? `${backend}${url}` : url;
  return token ? `${abs}${sep}auth=${encodeURIComponent(token)}` : abs;
}


export default function MyJobs() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const load = async () => {
    const { data } = await api.get("/complaints");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const open = list.filter((c) => !["Closed", "Cancelled"].includes(c.status));
  const closed = list.filter((c) => c.status === "Closed");

  return (
    <div className="space-y-5 max-w-2xl mx-auto" data-testid="my-jobs-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Jobs</h1>
        <p className="text-sm text-slate-500 mt-1">{user?.name} — {open.length} open · {closed.length} closed</p>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Open ({open.length})</div>
        <div className="space-y-3">
          {open.length === 0 && (
            <Card><CardContent className="p-6 text-center text-slate-400 text-sm">No open jobs.</CardContent></Card>
          )}
          {open.map((c) => <JobCard key={c.id} c={c} onDone={load} />)}
        </div>
      </div>

      {closed.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2 mt-6">Closed ({closed.length})</div>
          <div className="space-y-3">
            {closed.slice(0, 10).map((c) => <JobCard key={c.id} c={c} onDone={load} compact />)}
          </div>
        </div>
      )}
    </div>
  );
}


function JobCard({ c, onDone, compact }) {
  const [showHistory, setShowHistory] = useState(false);
  const visits = c.technician_visits || [];
  return (
    <Card data-testid={`job-card-${c.complaint_number}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/complaints/${c.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{c.complaint_number}</Link>
              <StatusBadge status={c.status} />
              <span className="text-xs text-slate-500 font-mono">{c.ageing_days != null ? `${c.ageing_days}d` : ""}</span>
              {visits.length > 0 && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{visits.length} visit{visits.length>1?"s":""}</span>}
            </div>
            <div className="text-sm font-medium text-slate-900 mt-1">{c.customer_name}</div>
            <div className="text-xs text-slate-600 mt-0.5">{c.brand} · {c.product_name}{c.product_model_number ? ` · ${c.product_model_number}` : ""}</div>
            <div className="text-xs text-slate-500 mt-1 flex items-start gap-1">
              <MapPin size={11} className="mt-0.5 flex-shrink-0" /> <span>{c.customer_address}</span>
            </div>
            <a href={`tel:${c.phone_number_1}`} className="text-xs text-blue-700 mt-1 inline-flex items-center gap-1 font-mono"
               data-testid={`call-${c.id}`}>
              <Phone size={11} /> {c.phone_number_1}
            </a>
            {c.details_for_technician && (
              <div className="text-xs text-slate-700 mt-2 italic bg-slate-50 border-l-2 border-slate-300 px-2 py-1">
                "{c.details_for_technician}"
              </div>
            )}
          </div>
        </div>

        {visits.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <button type="button" onClick={() => setShowHistory(!showHistory)}
                    className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                    data-testid={`toggle-history-${c.id}`}>
              <History size={12} /> {showHistory ? "Hide" : "Show"} visit history ({visits.length})
            </button>
            {showHistory && <VisitTimeline visits={visits} />}
          </div>
        )}

        {!compact && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
            <VisitDialog c={c} onDone={onDone} />
            <PartsDialog c={c} onDone={onDone} />
            <ConfirmReceivedButton c={c} onDone={onDone} />
            <UsePartButton c={c} onDone={onDone} />
            <ReturnPartButton c={c} onDone={onDone} />
            <CloseRequestButton c={c} onDone={onDone} />
            <Link to={`/complaints/${c.id}`}>
              <Button size="sm" variant="ghost">Details <ChevronRight size={14} /></Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function VisitTimeline({ visits }) {
  return (
    <div className="mt-2 space-y-2" data-testid="visit-timeline">
      {visits.map((v) => (
        <div key={v.id || v.visit_number} className="border-l-2 border-slate-300 pl-3 py-1 text-xs"
             data-testid={`visit-entry-${v.visit_number}`}>
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <span>Visit #{v.visit_number}</span>
            <span className="text-slate-500 font-normal">{v.visit_date}</span>
            {v.distance_km != null && <span className="text-slate-500 font-normal">· {v.distance_km} km</span>}
          </div>
          {v.technician_remarks && <div className="text-slate-700 mt-0.5">{v.technician_remarks}</div>}
          {v.diagnosis && <div className="text-slate-500 mt-0.5"><span className="font-semibold">Dx:</span> {v.diagnosis}</div>}
          {v.parts_used && v.parts_used.length > 0 && (
            <div className="text-slate-500 mt-0.5"><span className="font-semibold">Parts used:</span> {v.parts_used.map((p) => `${p.name}${p.qty?` × ${p.qty}`:""}`).join(", ")}</div>
          )}
          {v.serial_sticker_url && (
            <a href={authImg(v.serial_sticker_url)} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-blue-700 hover:underline mt-1"
               data-testid={`visit-${v.visit_number}-sticker`}>
              <ImageIcon size={11} /> Serial sticker
            </a>
          )}
          {v.photos && v.photos.length > 0 && (
            <div className="flex gap-1 mt-1">
              {v.photos.slice(0, 4).map((url, i) => (
                <a key={i} href={authImg(url)} target="_blank" rel="noopener noreferrer">
                  <img src={authImg(url)} alt="" className="h-10 w-10 object-cover rounded border" />
                </a>
              ))}
            </div>
          )}
          {v.latitude != null && v.longitude != null && (
            <a href={`https://maps.google.com/?q=${v.latitude},${v.longitude}`} target="_blank" rel="noopener noreferrer"
               className="text-blue-700 hover:underline mt-0.5 inline-block">📍 GPS</a>
          )}
          <div className="text-[10px] text-slate-400 mt-0.5">by {v.created_by_name}{v.on_behalf_of?` (on behalf of ${v.on_behalf_of})`:""}</div>
        </div>
      ))}
    </div>
  );
}


/** Mobile-friendly photo picker with explicit Gallery + Camera buttons.
 *  Calls onPick(eventLike) with the same shape as a standard input change event,
 *  so callers using onFile(key) can pass it straight through.
 */
function PhotoPicker({ testId, onPick, label, accent = "slate" }) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const palette = accent === "amber"
    ? "border-amber-300 text-amber-800 hover:bg-amber-100"
    : accent === "indigo"
    ? "border-indigo-300 text-indigo-800 hover:bg-indigo-100"
    : "border-slate-300 text-slate-800 hover:bg-slate-100";
  return (
    <div className="flex gap-2 mt-1">
      <button type="button"
              className={`flex-1 inline-flex items-center justify-center gap-1 border rounded-md py-2 text-xs font-semibold bg-white ${palette}`}
              onClick={() => galleryRef.current?.click()}
              data-testid={`${testId}-gallery-btn`}>
        <ImageIcon size={12} /> {label?.gallery || "Upload from Gallery"}
      </button>
      <button type="button"
              className={`flex-1 inline-flex items-center justify-center gap-1 border rounded-md py-2 text-xs font-semibold bg-white ${palette}`}
              onClick={() => cameraRef.current?.click()}
              data-testid={`${testId}-camera-btn`}>
        <Camera size={12} /> {label?.camera || "Take Photo"}
      </button>
      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
             onChange={onPick} data-testid={`${testId}-gallery-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
             onChange={onPick} data-testid={`${testId}-camera-input`} />
    </div>
  );
}



function VisitDialog({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const visits = c.technician_visits || [];
  const isFirstVisit = visits.length === 0 && !c.serial_sticker_url;
  const nextVisitNumber = visits.length + 1;
  const [form, setForm] = useState({
    visit_date: new Date().toISOString().slice(0, 10),
    technician_remarks: "",
    diagnosis: "",
    parts_required_flag: false,
    parts_required: "",
    part_name: "",
    part_code: "",
    parts_quantity: 1,
    parts_urgency: "normal",
    damaged_spare_photo_url: "",
    parts_used: [],
    serial_sticker_url: "",
    serial_number_1: "",
    photos: [],
    latitude: null,
    longitude: null,
    distance_km: "",
    distance_remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | requesting | granted | denied

  // Serial number is REQUIRED on Visit 1 (unless the complaint already has one saved).
  const hasExistingSerial = !!(c.serial_number_1 && c.serial_number_1.trim());
  const requireSerialInput = isFirstVisit && !hasExistingSerial;

  useEffect(() => {
    if (!open) return;
    setForm({
      visit_date: new Date().toISOString().slice(0, 10),
      technician_remarks: "", diagnosis: "",
      parts_required_flag: false, parts_required: "",
      part_name: "", part_code: "", parts_quantity: 1, parts_urgency: "normal",
      damaged_spare_photo_url: "",
      parts_used: [], serial_sticker_url: "",
      serial_number_1: c.serial_number_1 || "",
      photos: [],
      latitude: null, longitude: null, distance_km: "", distance_remarks: "",
    });
    setGpsStatus("idle");
  }, [open, c.serial_number_1]);

  const captureGps = () => {
    if (!navigator.geolocation) { setGpsStatus("denied"); toast.error("GPS not supported on this device"); return; }
    setGpsStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        setGpsStatus("granted");
        toast.success(`GPS captured: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
      },
      (err) => { setGpsStatus("denied"); toast.error("GPS unavailable", { description: err.message }); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const onFile = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB. Use compressor or take new photo at lower resolution."); return; }
    // Phase prod-fix — upload binary FIRST, then attach URL only (no base64 in
    // complaint document → fixes 'update command document too large' on Mongo).
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/visit-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.image_url;
      if (key === "photos") setForm((f) => ({ ...f, photos: [...f.photos, url] }));
      else setForm((f) => ({ ...f, [key]: url }));
    } catch (err) {
      toast.error("Photo upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) || "Try again with a smaller image." });
    } finally {
      // allow re-uploading same file
      if (e.target) e.target.value = "";
    }
  };

  const submit = async () => {
    if (!form.technician_remarks.trim()) { toast.error("Technician remarks are required"); return; }
    if (isFirstVisit && !form.serial_sticker_url) {
      toast.error("Serial number sticker photo is required for the first visit"); return;
    }
    if (requireSerialInput && !form.serial_number_1.trim()) {
      toast.error("Please enter the serial number from the sticker"); return;
    }
    if (form.parts_required_flag) {
      if (!form.parts_required.trim() && !form.part_name.trim()) {
        toast.error("Describe the part required (description or part name)"); return;
      }
      if (!form.damaged_spare_photo_url) {
        toast.error("Damaged spare part photo is required when requesting a spare"); return;
      }
    }
    if (form.distance_km && form.latitude == null && form.longitude == null && !form.distance_remarks.trim()) {
      toast.error("Manual distance entry requires a remarks note"); return;
    }
    setBusy(true);
    try {
      const payload = {
        visit_date: form.visit_date,
        technician_remarks: form.technician_remarks,
        diagnosis: form.diagnosis,
        parts_required_flag: form.parts_required_flag,
        parts_required: form.parts_required_flag ? form.parts_required : "",
        part_name: form.parts_required_flag ? form.part_name : undefined,
        part_code: form.parts_required_flag ? form.part_code : undefined,
        parts_quantity: form.parts_required_flag ? Number(form.parts_quantity) || 1 : undefined,
        parts_urgency: form.parts_required_flag ? form.parts_urgency : undefined,
        damaged_spare_photo_url: form.parts_required_flag ? form.damaged_spare_photo_url : undefined,
        parts_used: form.parts_used,
        serial_sticker_url: form.serial_sticker_url || undefined,
        serial_number_1: form.serial_number_1.trim() || undefined,
        photos: form.photos,
        latitude: form.latitude,
        longitude: form.longitude,
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
        distance_remarks: form.distance_remarks || undefined,
      };
      const { data } = await api.post(`/complaints/${c.id}/visit-update`, payload);
      toast.success(`Visit #${data.visit_number} saved`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`my-visit-${c.id}`}>
          <Wrench size={13} className="mr-1" /> {visits.length === 0 ? "Log Visit 1" : `Add Visit ${nextVisitNumber}`}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Visit #{nextVisitNumber} · {c.complaint_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Visit Date</Label>
            <Input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid={`visit-date-${c.id}`} /></div>
          <div><Label>Technician Remarks <span className="text-rose-600">*</span></Label>
            <Textarea value={form.technician_remarks} onChange={(e) => setForm({ ...form, technician_remarks: e.target.value })} rows={3}
                      data-testid={`visit-remarks-${c.id}`} placeholder="What did you find / do?" /></div>
          <div><Label>Diagnosis</Label>
            <Input value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} data-testid={`visit-diagnosis-${c.id}`} /></div>
          <div><Label>Parts Required (for next visit)</Label>
            <Textarea value={form.parts_required} onChange={(e) => setForm({ ...form, parts_required: e.target.value })} rows={2} /></div>
          <PartsUsedField parts={form.parts_used} onChange={(arr) => setForm({ ...form, parts_used: arr })} />

          {isFirstVisit && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 space-y-2">
              <Label>Serial Number Sticker Photo <span className="text-rose-600">*</span></Label>
              <PhotoPicker testId={`visit-sticker-${c.id}`} accent="amber"
                            onPick={onFile("serial_sticker_url")} />
              {form.serial_sticker_url && (
                <img src={authImg(form.serial_sticker_url)} alt="sticker" className="h-16 mt-1 rounded border" />
              )}
              <div>
                <Label>
                  Serial Number {requireSerialInput && <span className="text-rose-600">*</span>}
                </Label>
                <Input
                  value={form.serial_number_1}
                  onChange={(e) => setForm({ ...form, serial_number_1: e.target.value })}
                  placeholder={hasExistingSerial ? "Already captured — edit if needed" : "Type the serial as shown on the sticker"}
                  data-testid={`visit-serial-${c.id}`}
                  autoCapitalize="characters"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {hasExistingSerial
                    ? "Serial already on file. Leave as-is or correct it."
                    : "Required: enter the serial exactly as shown. Case/spaces are normalised automatically."}
                </p>
              </div>
              <p className="text-[10px] text-slate-500">Serial sticker photo + serial number are required on the first visit so the system can detect duplicate complaints.</p>
            </div>
          )}

          <div className="bg-indigo-50 border border-indigo-200 rounded p-3 space-y-2">
            <Label className="text-sm font-semibold">Visit Diagnosis & Parts Requirement</Label>
            <div>
              <Label className="text-xs">Diagnosis (optional)</Label>
              <Textarea rows={2} value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                        placeholder="What did you find?" data-testid={`visit-diagnosis-${c.id}`} />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input type="checkbox" id={`partsreq-${c.id}`} checked={form.parts_required_flag}
                     onChange={(e) => setForm({ ...form, parts_required_flag: e.target.checked })}
                     data-testid={`visit-parts-flag-${c.id}`} />
              <label htmlFor={`partsreq-${c.id}`} className="text-sm font-semibold">Parts required for repair</label>
            </div>
            {form.parts_required_flag && (
              <div className="space-y-2 pl-2 border-l-2 border-indigo-300">
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input value={form.parts_required} onChange={(e) => setForm({ ...form, parts_required: e.target.value })}
                         placeholder="e.g. compressor failed, needs replacement"
                         data-testid={`visit-parts-desc-${c.id}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Part Name (if known)</Label>
                    <Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })}
                           data-testid={`visit-part-name-${c.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Part Code</Label>
                    <Input value={form.part_code} onChange={(e) => setForm({ ...form, part_code: e.target.value })}
                           data-testid={`visit-part-code-${c.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" min={1} value={form.parts_quantity}
                           onChange={(e) => setForm({ ...form, parts_quantity: parseInt(e.target.value) || 1 })}
                           data-testid={`visit-part-qty-${c.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Urgency</Label>
                    <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                            value={form.parts_urgency}
                            onChange={(e) => setForm({ ...form, parts_urgency: e.target.value })}
                            data-testid={`visit-part-urgency-${c.id}`}>
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Damaged Spare Photo <span className="text-rose-600">*</span></Label>
                  <PhotoPicker testId={`visit-damaged-photo-${c.id}`} accent="indigo"
                                onPick={onFile("damaged_spare_photo_url")} />
                  {form.damaged_spare_photo_url && (
                    <img src={authImg(form.damaged_spare_photo_url)} alt="damaged" className="h-16 mt-1 rounded border" />
                  )}
                  <p className="text-[10px] text-slate-500 mt-1">
                    A photo of the damaged spare is required so Spares can verify before dispatch.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Service Photos</Label>
            <PhotoPicker testId={`visit-photos-${c.id}`} accent="slate"
                          onPick={onFile("photos")} />
            <p className="text-[10px] text-slate-500 mt-1">Photos are uploaded one at a time to keep complaints fast on slow mobile networks.</p>
            {form.photos.length > 0 && (
              <div className="flex gap-1 flex-wrap mt-1">
                {form.photos.map((u, i) => (
                  <div key={i} className="relative">
                    <img src={authImg(u)} alt="" className="h-12 w-12 object-cover rounded border" />
                    <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                            className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] rounded-full h-4 w-4">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-2 space-y-2">
            <Label className="text-xs">Location & Distance</Label>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={captureGps} disabled={gpsStatus === "requesting"} data-testid={`visit-gps-${c.id}`}>
                <Crosshair size={12} className="mr-1" /> {gpsStatus === "requesting" ? "Locating…" : "Capture GPS"}
              </Button>
              {gpsStatus === "granted" && form.latitude != null && (
                <span className="text-[11px] text-emerald-700 font-mono">📍 {form.latitude.toFixed(4)},{form.longitude.toFixed(4)}</span>
              )}
              {gpsStatus === "denied" && (
                <span className="text-[11px] text-rose-700">GPS unavailable — use manual entry below</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Distance (km)</Label>
                <Input type="number" step="0.1" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })}
                       data-testid={`visit-distance-${c.id}`} />
              </div>
              {form.distance_km && form.latitude == null && (
                <div>
                  <Label className="text-xs">Distance Remarks <span className="text-rose-600">*</span></Label>
                  <Input value={form.distance_remarks} onChange={(e) => setForm({ ...form, distance_remarks: e.target.value })}
                         placeholder="e.g. odometer reading" data-testid={`visit-distance-remarks-${c.id}`} />
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white" data-testid={`visit-save-${c.id}`}>
            {busy ? "Saving..." : `Save Visit #${nextVisitNumber}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PartsUsedField({ parts, onChange }) {
  const [draft, setDraft] = useState({ name: "", qty: 1 });
  const add = () => {
    if (!draft.name.trim()) return;
    onChange([...parts, { name: draft.name.trim(), qty: parseInt(draft.qty) || 1 }]);
    setDraft({ name: "", qty: 1 });
  };
  return (
    <div>
      <Label>Parts Used (this visit)</Label>
      <div className="flex gap-2 mt-1">
        <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Part name / model" className="flex-1" />
        <Input type="number" min={1} value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} className="w-16" />
        <Button type="button" variant="outline" size="sm" onClick={add}>Add</Button>
      </div>
      {parts.length > 0 && (
        <div className="mt-1 space-y-1">
          {parts.map((p, i) => (
            <div key={i} className="text-xs flex justify-between items-center bg-slate-50 px-2 py-1 rounded">
              <span>{p.name} × {p.qty}</span>
              <button type="button" onClick={() => onChange(parts.filter((_, j) => j !== i))} className="text-rose-600">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function PartsDialog({ c, onDone }) {
  // Technicians can request parts but CANNOT mark PNA (server enforces; we hide the checkbox).
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState("");
  const [remarks, setRemarks] = useState("");
  const submit = async () => {
    if (!parts.trim()) { toast.error("List the parts needed"); return; }
    try {
      await api.post(`/complaints/${c.id}/request-parts`, { parts_required: parts, technician_remarks: remarks });
      toast.success("Parts requested");
      setOpen(false); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`my-parts-${c.id}`}>
          <PackagePlus size={13} className="mr-1" /> Request Parts
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Request Parts · {c.complaint_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Parts Required</Label><Textarea value={parts} onChange={(e) => setParts(e.target.value)} rows={3} data-testid={`parts-required-${c.id}`} /></div>
          <div><Label>Notes</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid={`parts-submit-${c.id}`}>Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CloseRequestButton({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    final_technician_remarks: "",
    service_performed: "",
    parts_used_notes: "",
    work_completed: true,
  });
  // Don't show the button if closure pipeline already in flight or finished.
  const HIDE = new Set(["Closed", "Request to Close",
    "Request to Close - Pending Service Manager Verification",
    "Pending CCR Happy Call", "Pending Service Head Closure"]);
  if (HIDE.has(c.status)) return null;
  const submit = async () => {
    if (!form.final_technician_remarks.trim()) {
      toast.error("Final remarks are required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/complaints/${c.id}/request-close`, {
        final_technician_remarks: form.final_technician_remarks,
        service_performed: form.service_performed || undefined,
        parts_used_notes: form.parts_used_notes || undefined,
        work_completed: form.work_completed,
      });
      toast.success("Closure requested · awaiting Service Manager verification");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`my-close-${c.id}`}>
        <CheckCircle2 size={13} className="mr-1" /> Request Close
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Closure · {c.complaint_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <Label className="text-xs">Final Remarks <span className="text-rose-600">*</span></Label>
            <Textarea rows={3} value={form.final_technician_remarks}
                      onChange={(e) => setForm({ ...form, final_technician_remarks: e.target.value })}
                      placeholder="Summarise the work done" data-testid={`close-final-remarks-${c.id}`} />
            <Label className="text-xs">Service Performed</Label>
            <Input value={form.service_performed} onChange={(e) => setForm({ ...form, service_performed: e.target.value })}
                   placeholder="e.g. Compressor replaced and gas charged"
                   data-testid={`close-service-${c.id}`} />
            <Label className="text-xs">Parts Used / Returned Notes</Label>
            <Textarea rows={2} value={form.parts_used_notes}
                      onChange={(e) => setForm({ ...form, parts_used_notes: e.target.value })}
                      placeholder="e.g. Display panel installed; old panel returned to spares"
                      data-testid={`close-parts-notes-${c.id}`} />
            <div className="flex items-center gap-2 pt-1">
              <input id={`work-completed-${c.id}`} type="checkbox" checked={form.work_completed}
                     onChange={(e) => setForm({ ...form, work_completed: e.target.checked })}
                     data-testid={`close-work-completed-${c.id}`} />
              <Label htmlFor={`work-completed-${c.id}`} className="text-xs cursor-pointer">Work fully completed</Label>
            </div>
            <p className="text-[11px] text-amber-700 pt-2">
              Payment, TADA, distance and self-purchase will be verified and set by the Service Manager.
              Closure flows: Service Manager → CCR Happy Call → Service Head for final closure.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white" data-testid={`close-submit-${c.id}`}>
              {busy ? "Submitting..." : "Request Closure"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



// ============================================================================
// Confirm Received / Use Part / Return Damaged — technician parts lifecycle
// ============================================================================

/** Loads dispatches for this complaint via the technician's own ledger. */
function useDispatches(complaintId, open) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/technician/parts-ledger");
      setRows((data.rows || []).filter((r) => r.complaint_id === complaintId));
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open, complaintId]);
  return { rows, loading, reload };
}

function ConfirmReceivedButton({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState("");
  const pr = c.parts_request || {};
  const pendingReceipt = pr.dispatched_at && !c.received_by_technician_date;
  const inTransit = ["Parts in Transit", "Parts Sent", "Parts Delivered"].includes(c.status);
  if (!(pendingReceipt || inTransit)) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const body = {};
      if (pr.dispatch_id) body.dispatch_id = pr.dispatch_id;
      else if (pr.dispatch_pod_number) body.pod_number = pr.dispatch_pod_number;
      if (remarks.trim()) body.remarks = remarks.trim();
      await api.post(`/complaints/${c.id}/confirm-received`, body);
      toast.success("Receipt confirmed");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setOpen(true)} data-testid={`my-confirm-received-${c.id}`}>
        <CheckCircle2 size={13} className="mr-1" /> Confirm Received
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`confirm-received-dialog-${c.id}`}>
          <DialogHeader>
            <DialogTitle>Confirm Parts Received · {c.complaint_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="text-xs text-slate-500">
              POD <span className="font-mono">{pr.dispatch_pod_number || "—"}</span>
              {pr.part_name && <> · {pr.part_name}</>}
              {pr.dispatched_qty && <> · qty {pr.dispatched_qty}</>}
            </div>
            <Label className="text-xs">Receipt Remarks (optional)</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Condition / packaging notes"
                      data-testid={`my-confirm-received-remarks-${c.id}`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid={`my-confirm-received-submit-${c.id}`}>
              {busy ? "Saving…" : "Confirm Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UsePartButton({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const { rows: dispatches } = useDispatches(c.id, open);
  // Quick predicate to decide whether to even show the button.
  const canShow = c.received_by_technician_date && c.status !== "Closed";
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ dispatch_id: "", qty_used: 1, remarks: "" });
  // Auto-pick the first received dispatch with remaining qty when dialog opens.
  useEffect(() => {
    if (open && dispatches.length) {
      const useable = dispatches.find((d) => d.qty_remaining > 0
                                              && d.status !== "Pending Receipt");
      if (useable && !form.dispatch_id) setForm((f) => ({ ...f, dispatch_id: useable.dispatch_id }));
    }
  }, [open, dispatches]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canShow) return null;
  const sel = dispatches.find((d) => d.dispatch_id === form.dispatch_id);
  const submit = async () => {
    if (!form.dispatch_id) { toast.error("Pick a dispatched part"); return; }
    if (Number(form.qty_used) < 1) { toast.error("Quantity must be >= 1"); return; }
    setBusy(true);
    try {
      await api.post(`/complaints/${c.id}/use-part`, {
        dispatch_id: form.dispatch_id,
        qty_used: Number(form.qty_used),
        part_name: sel?.part_name,
        part_code: sel?.part_code,
        remarks: form.remarks || undefined,
      });
      toast.success("Part marked as used");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setOpen(true)} data-testid={`my-use-part-${c.id}`}>
        <Wrench size={13} className="mr-1" /> Use Part
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`my-use-part-dialog-${c.id}`}>
          <DialogHeader>
            <DialogTitle>Use Spare Part · {c.complaint_number}</DialogTitle>
          </DialogHeader>
          {dispatches.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              No dispatched parts found for this complaint.
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <Label className="text-xs">Dispatched Part</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={form.dispatch_id}
                      onChange={(e) => setForm({ ...form, dispatch_id: e.target.value })}
                      data-testid={`my-use-part-select-${c.id}`}>
                <option value="">— select —</option>
                {dispatches.map((d) => (
                  <option key={d.dispatch_id} value={d.dispatch_id}
                          disabled={d.status === "Pending Receipt" || d.qty_remaining < 1}>
                    {d.pod_number} · {d.part_name} ({d.part_code || "—"}) · remaining {d.qty_remaining}/{d.qty_sent}
                    {d.status === "Pending Receipt" && " · not yet received"}
                  </option>
                ))}
              </select>
              {sel && (
                <div className="text-[11px] text-slate-500">
                  Available to use: <span className="font-semibold">{sel.qty_remaining}</span>
                </div>
              )}
              <Label className="text-xs">Quantity Used</Label>
              <Input type="number" min={1} max={sel?.qty_remaining || 99} value={form.qty_used}
                     onChange={(e) => setForm({ ...form, qty_used: e.target.value })}
                     data-testid={`my-use-part-qty-${c.id}`} />
              <Label className="text-xs">Remarks (optional)</Label>
              <Textarea rows={2} value={form.remarks}
                        onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        placeholder="e.g. Installed and tested"
                        data-testid={`my-use-part-remarks-${c.id}`} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.dispatch_id}
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                    data-testid={`my-use-part-submit-${c.id}`}>
              {busy ? "Saving…" : "Mark Used"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReturnPartButton({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const { rows: dispatches } = useDispatches(c.id, open);
  const canShow = c.received_by_technician_date && c.status !== "Closed";
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    dispatch_id: "", qty_returned: 1,
    condition: "Damaged", remarks: "", photo_url: "",
  });
  useEffect(() => {
    if (open && dispatches.length && !form.dispatch_id) {
      const any = dispatches[0];
      if (any) setForm((f) => ({ ...f, dispatch_id: any.dispatch_id }));
    }
  }, [open, dispatches]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!canShow) return null;

  const sel = dispatches.find((d) => d.dispatch_id === form.dispatch_id);
  const readFileAsDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Photo must be an image"); return; }
    if (f.size > 4_500_000) { toast.error("Photo too large"); return; }
    const dataUrl = await readFileAsDataUrl(f);
    setForm((s) => ({ ...s, photo_url: dataUrl }));
  };

  const submit = async () => {
    if (!form.dispatch_id) { toast.error("Pick a dispatch"); return; }
    if (Number(form.qty_returned) < 1) { toast.error("Quantity must be >= 1"); return; }
    if (form.condition === "Damaged" && !form.photo_url) {
      toast.error("Photo of damaged part is required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/complaints/${c.id}/return-part`, {
        dispatch_id: form.dispatch_id,
        qty_returned: Number(form.qty_returned),
        condition: form.condition,
        part_name: sel?.part_name,
        part_code: sel?.part_code,
        remarks: form.remarks || undefined,
        photo_url: form.photo_url || undefined,
      });
      toast.success("Return submitted · pending Spares confirmation");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => setOpen(true)} data-testid={`my-return-part-${c.id}`}>
        <PackagePlus size={13} className="mr-1" /> Return Damaged Part
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`my-return-part-dialog-${c.id}`}>
          <DialogHeader>
            <DialogTitle>Return Damaged/Replaced Part · {c.complaint_number}</DialogTitle>
          </DialogHeader>
          {dispatches.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              No dispatched parts found for this complaint.
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <Label className="text-xs">Linked Dispatch</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={form.dispatch_id}
                      onChange={(e) => setForm({ ...form, dispatch_id: e.target.value })}
                      data-testid={`my-return-part-select-${c.id}`}>
                <option value="">— select —</option>
                {dispatches.map((d) => (
                  <option key={d.dispatch_id} value={d.dispatch_id}>
                    {d.pod_number} · {d.part_name} ({d.part_code || "—"}) ·
                    sent {d.qty_sent} · used {d.qty_used} · ret {d.qty_returned_pending + d.qty_returned_confirmed}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Quantity Returning</Label>
                  <Input type="number" min={1} value={form.qty_returned}
                         onChange={(e) => setForm({ ...form, qty_returned: e.target.value })}
                         data-testid={`my-return-part-qty-${c.id}`} />
                </div>
                <div>
                  <Label className="text-xs">Condition</Label>
                  <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                          value={form.condition}
                          onChange={(e) => setForm({ ...form, condition: e.target.value })}
                          data-testid={`my-return-part-condition-${c.id}`}>
                    {["Damaged", "Defective", "Used", "Replaced", "Other"].map((x) =>
                      <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              </div>
              <Label className="text-xs">Photo {form.condition === "Damaged" && <span className="text-rose-600">*</span>}</Label>
              <Input type="file" accept="image/*" onChange={onPhoto}
                     data-testid={`my-return-part-photo-${c.id}`} />
              {form.photo_url && (
                <img src={form.photo_url} alt="" className="h-16 w-16 object-cover rounded border" />
              )}
              <Label className="text-xs">Return Remarks</Label>
              <Textarea rows={2} value={form.remarks}
                        onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        placeholder="e.g. compressor burnt at terminals"
                        data-testid={`my-return-part-remarks-${c.id}`} />
              <p className="text-[11px] text-amber-700">
                Spares Assistant will confirm the return — damaged items will go to the Damaged Stock bucket, not Fresh.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.dispatch_id}
                    className="bg-amber-600 text-white hover:bg-amber-700"
                    data-testid={`my-return-part-submit-${c.id}`}>
              {busy ? "Saving…" : "Submit Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
